import React, { useState, useRef, useEffect, ReactNode } from "react";
import i18next, { useTranslation } from "utilities/i18n";
import {
  useUpdatePolicyMutation,
  useCreatePolicyMutation,
  PolicyInput,
  PolicyDeploymentScope,
  useProvisioningSyncMutation,
  useDeletePolicyMutation,
  SwUpdateSource,
  FeatureCategory,
  EnumVal,
  SoftwareSupportUpdatePath,
  PolicyFragment
} from "generated/graphql";
import {
  Form,
  Select,
  ToggleSwitch,
  Input,
  UnsafeFormik,
  useField,
  useFormikContext
} from "collections/Form";
import { FormikErrors } from "formik/dist/types";
import {
  useQueryParamString,
  useQueryParamBoolean,
  NavLink,
  Switch,
  Route,
  Redirect,
  generatePath
} from "polyrouter";
import {
  setProvisionedDevicePolicyScope,
  setProvisionedSettings,
  SettingChangeIndicator,
  useProvisionedDevicePolicyScopeContext,
  useProvisionedSettingsContext
} from "./ProvisionedDeviceSettingsRouter";
import {
  AUTO_UPDATE_LATEST,
  CALL_CONFIGURATION,
  CUSTOM_SUPPORT_UPDATE_PATH,
  CUSTOM_UPDATE_PATH,
  DATE_FORMAT,
  DEVICE_PARAM_TYPE,
  GLOBAL_SETTINGS,
  LATEST,
  LINE_LIMIT_PARAM,
  MAIN,
  NO_GROUPING,
  PARAM_TYPE,
  POLLING_INTERVAL,
  SCHEDULE_UPDATE_CHECKS,
  UPDATE_PATH,
  UPDATE_SOURCE,
  UPDATE_START_TIME,
  UPDATE_STOP_TIME
} from "Constants";
import {
  useNotification,
  DISMISS,
  NewNotification
} from "collections/Notifications";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store";
import {
  NoResultsIndicator,
  ToolTip,
  PolyIconButton,
  PolyIcon,
  BetaBadge,
  PreviewBadge,
  LoadingIndicator
} from "elements";
import { OptionType } from "elements/DropdownList";
import {
  SettingsCategory,
  ErrorDisplay,
  InactiveInputToolTip,
  useInheritanceLabel
} from "../Settings-Shared/DeviceSettingsComponents";
import "routes/Manage/Inventory/View/DeviceDetails.scss";
import { DetailProps } from "routes/Manage/Inventory/View/DeviceDetails";
import { Layout } from "views/Main/Layouts";
import { debounce } from "lodash";
import { settingChanged, resetChanges, Change } from "store/settings";
import { useAuth0 } from "@auth0/auth0-react";
import { ModalConfirm, useModalDispatch } from "collections/Modal";
import { needs, useGuard } from "rbac";
import { DeviceHeader } from "../DeviceHeader";
import MySettings, { getUnsavedChangesCount } from "./MySettings";
import ActiveCallConfirmModal, {
  useActiveCallModalActions
} from "./ActiveCallConfirmModal";
import { CombinedError } from "urql";
import {
  ProvisionedUpdateSoftware,
  provisionedUpdateSchema,
  ProvisionedUpdateFormType
} from "./ProvisionedUpdateSoftware";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import { format } from "date-fns";
import {
  convertValueToString,
  createConfigurationAttribute,
  getChangedSettings,
  getConfigurationAttributesInput,
  getDeviceModel,
  getDeviceSettingName,
  getFlatSettings,
  getScope,
  getSupportUpdatePathInput,
  isDeviceSwVersionChanged,
  updateConfigurationAttribute
} from "./utils/provisionedDeviceSettingsUtils";
import {
  Setting,
  SettingsTree,
  SettingsValues,
  useProvisionedDeviceSettings
} from "./hooks/useProvisionedDeviceSettings";
import { useSettingsPolicy, useSoftwarePolicy } from "./hooks/usePolicies";
import { useExternalPolicy } from "./hooks/useExternalPolicy";
import { useDeviceDetails } from "../View/DeviceDetailsRouter";
import { resetProvisionedDeviceSettingInheritance } from "store/provisionedDeviceSettingInheritance";

const DEFAULT_SUBGROUPING = "default";

export const getSuccessNotification = (
  deploymentScope: PolicyDeploymentScope,
  policyCreated: boolean
): NewNotification => ({
  id: "save-policy-success",
  type: "success",
  message: policyCreated
    ? i18next.t("POLICY_CREATED")
    : i18next.t("POLICY_SAVED", {
        policy: i18next.t(`${deploymentScope}_POLICY` as const)
      }),
  dismiss: DISMISS.DEFAULT
});

export const getPolicyTitle = (
  deploymentScope: PolicyDeploymentScope | null,
  entityName?: string
): string => {
  let policyTitle = "";
  if (deploymentScope === PolicyDeploymentScope.DeviceModel) {
    policyTitle = i18next.t("DEVICE_MODEL_POLICY");
  } else if (deploymentScope === PolicyDeploymentScope.Site) {
    policyTitle = `${i18next.t("SITE_POLICY")} | ${entityName}`;
  } else if (deploymentScope === PolicyDeploymentScope.Group) {
    policyTitle = `${i18next.t("GROUP_POLICY")} | ${entityName}`;
  } else if (deploymentScope === PolicyDeploymentScope.Device) {
    policyTitle = `${i18next.t("DEVICE_POLICY")} | ${entityName}`;
  }

  return policyTitle;
};

const DeviceSettingsLoader: React.FC<{
  model: string;
  deviceId?: string;
  siteId?: string;
  siteName?: string;
  groupId?: string;
  groupName?: string;
  isPolicyView: boolean;
}> = ({
  model,
  deviceId,
  siteId,
  siteName,
  groupId,
  groupName,
  isPolicyView
}) => {
  const tenantId = useTenantId();
  const [
    { policyScope },
    dispatchScopeContext
  ] = useProvisionedDevicePolicyScopeContext();
  const [
    {
      maxConfiguredLine,
      externalPolicyLastSuccessfullyUpdated,
      externalPolicyLastSuccessfullyUpdatedTime
    },
    dispatchSettingsContext
  ] = useProvisionedSettingsContext();

  const scope = getScope(deviceId, siteId, groupId);

  useEffect(() => {
    if (scope !== policyScope) {
      dispatchScopeContext(setProvisionedDevicePolicyScope(scope));
    }
  }, [dispatchScopeContext, scope, policyScope]);

  const deviceModel = getDeviceModel(model);
  const {
    deviceParameters,
    deviceSettings,
    values,
    defaultValues,
    numOfLines,
    msTeamsAssistedProvisioningEnabled,
    fetching: fetchingDeviceSettings
  } = useProvisionedDeviceSettings(
    deviceModel,
    scope,
    deviceId,
    siteId,
    groupId
  );

  const { data: deviceData, fetching: fetchingDevice } = useDeviceDetails(
    deviceId ?? ""
  );
  const device = deviceData?.devices[0];

  const {
    policy: settingsPolicy,
    fetching: fetchingSettingsPolicy
  } = useSettingsPolicy({
    model,
    tenantId,
    device,
    siteId,
    groupId,
    msTeamsAssistedProvisioningEnabled
  });

  const {
    policy: swUpdatePolicy,
    updateSettings: swUpdateSettings,
    updateValues: swUpdateValues,
    fetching: fetchingSwPolicy
  } = useSoftwarePolicy({
    model,
    deviceParameters,
    tenantId,
    device,
    siteId,
    groupId,
    msTeamsAssistedProvisioningEnabled
  });

  // In case of Device Level settings check for external policy status
  const {
    policy: externalPolicy,
    fetching: fetchingExternalPolicy
  } = useExternalPolicy({
    checkExternalPolicy: !isPolicyView && !!deviceId,
    tenantId,
    deviceId
  });

  useEffect(() => {
    if (numOfLines.maxConfiguredLine !== maxConfiguredLine) {
      dispatchSettingsContext(
        setProvisionedSettings({
          maxConfiguredLine: numOfLines.maxConfiguredLine
        })
      );
    }
  }, [
    dispatchSettingsContext,
    maxConfiguredLine,
    numOfLines.maxConfiguredLine
  ]);

  useEffect(() => {
    if (
      externalPolicy &&
      (externalPolicy.lastSuccessfullyUpdated !==
        externalPolicyLastSuccessfullyUpdated ||
        externalPolicy.lastSuccessfullyUpdatedTime !==
          externalPolicyLastSuccessfullyUpdatedTime)
    ) {
      dispatchSettingsContext(
        setProvisionedSettings({
          externalPolicyLastSuccessfullyUpdated:
            externalPolicy?.lastSuccessfullyUpdated,
          externalPolicyLastSuccessfullyUpdatedTime:
            externalPolicy?.lastSuccessfullyUpdatedTime
        })
      );
    }
  }, [
    dispatchSettingsContext,
    externalPolicy,
    externalPolicyLastSuccessfullyUpdated,
    externalPolicyLastSuccessfullyUpdatedTime
  ]);

  const fetching =
    fetchingDeviceSettings ||
    fetchingDevice ||
    fetchingSettingsPolicy ||
    fetchingSwPolicy ||
    fetchingExternalPolicy;

  if (fetching) {
    return <LoadingIndicator />;
  }

  if (!settingsPolicy || !swUpdatePolicy) {
    return null;
  }

  return (
    <DeviceSettings
      settings={deviceSettings}
      swUpdateSettings={swUpdateSettings}
      values={{ ...values, ...swUpdateValues }}
      defaultValues={defaultValues}
      settingsPolicy={settingsPolicy}
      swUpdatePolicy={swUpdatePolicy}
      device={device}
      siteName={siteName}
      groupName={groupName}
      isPolicyView={isPolicyView}
    />
  );
};

const DeviceSettings: React.FC<{
  settings: SettingsTree;
  swUpdateSettings: Record<string, SectionSetting>;
  values: SettingsValues;
  defaultValues: SettingsValues;
  settingsPolicy: PolicyFragment;
  swUpdatePolicy: PolicyFragment;
  isPolicyView: boolean;
  device?: DetailProps;
  siteName?: string;
  groupName?: string;
}> = ({
  settings,
  swUpdateSettings,
  values,
  defaultValues,
  settingsPolicy,
  swUpdatePolicy,
  isPolicyView,
  device,
  siteName,
  groupName
}) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const dispatch = useDispatch();
  const closeModal = useModalDispatch();
  const state = useSelector((s: State) => s);
  const { settings: unsavedSettings } = state;

  const [settingFilter] = useQueryParamString<string>("setting");
  const [showLineWarning] = useQueryParamBoolean<string>("warning");
  const tenantId = useTenantId();
  const { user } = useAuth0();

  const [, updatePolicy] = useUpdatePolicyMutation();
  const [, createPolicy] = useCreatePolicyMutation();
  const [, deletePolicy] = useDeletePolicyMutation();
  const [, provisioningSync] = useProvisioningSyncMutation();
  const { activeCallInterrupt, ...rest } = useActiveCallModalActions<
    SettingsValues
  >(device?.id);

  const validate = (values: SettingsValues) => {
    let errors: FormikErrors<SettingsValues> = {};
    // we should only validate settings which were changed
    const changedSettings = getChangedSettings(values, unsavedSettings);
    const flatSettings = getFlatSettings(settings);

    // if there is any changed SW Update setting, we should validate entire SW Update form
    // if no changes, don't validate
    let validateSWUpdate = false;
    Object.values(changedSettings).forEach(
      ({ name: settingName, value: settingValue, change }) => {
        if (change === "edited") {
          const setting = flatSettings[settingName];
          if (setting) {
            const { settingName, type, max, min } = setting;
            let valueToCheck;
            let errorTextOverflow = "";
            let errorTextUnderflow = "";

            switch (type) {
              case DEVICE_PARAM_TYPE.UINT:
              case DEVICE_PARAM_TYPE.UINT_NULL:
              case DEVICE_PARAM_TYPE.SINT:
              case DEVICE_PARAM_TYPE.SINT_NULL:
                if (typeof settingValue === "string") {
                  const numValue = parseInt(settingValue);
                  if (!isNaN(numValue)) {
                    valueToCheck = numValue;
                  }
                } else if (typeof settingValue === "number") {
                  valueToCheck = settingValue;
                }

                errorTextOverflow = t("FORM_VALIDATE_RANGE_OVERFLOW", { max });
                errorTextUnderflow = t("FORM_VALIDATE_RANGE_UNDERFLOW", {
                  min
                });

                break;
              case DEVICE_PARAM_TYPE.STRING:
              case DEVICE_PARAM_TYPE.STRING_PASSWORD:
                valueToCheck = (settingValue as string).length;
                errorTextOverflow = t("FORM_VALIDATE_TOO_LONG", {
                  minLength: max
                });
                errorTextUnderflow = t("FORM_VALIDATE_TOO_SHORT", {
                  maxLength: min
                });

                break;
            }

            if (
              valueToCheck !== undefined &&
              max != null &&
              valueToCheck > max
            ) {
              errors[settingName] = errorTextOverflow;
            } else if (
              valueToCheck !== undefined &&
              min != null &&
              valueToCheck < min
            ) {
              errors[settingName] = errorTextUnderflow;
            }
          }

          // flag SW Update form for validation if changed setting is one of SW Update settings
          validateSWUpdate =
            validateSWUpdate ||
            Object.keys(provisionedUpdateSchema.fields).includes(settingName);
        }
      }
    );

    if (validateSWUpdate) {
      // validate values against SW Update schema (only if not reverted)
      Object.keys(provisionedUpdateSchema.fields).forEach(setting => {
        const reverted = changedSettings[setting]?.change === "reverted";
        if (!reverted) {
          try {
            provisionedUpdateSchema.validateSyncAt(
              setting,
              values as ProvisionedUpdateFormType
            );
          } catch (err) {
            errors[err.path] = err.message;
          }
        }
      });
    }

    return errors;
  };

  const submit = async (values: SettingsValues) => {
    // Prepare Settings policy
    let configurationAttributes = getConfigurationAttributesInput(
      settingsPolicy?.configurationAttributes ?? []
    );
    // we should only save settings which were changed
    const changedSettings = getChangedSettings(values, unsavedSettings);
    const flatSettings = getFlatSettings(settings);

    let updatePolicyChange: Change | null = null;
    Object.values(changedSettings).forEach(
      ({ name: settingName, value: settingValue, change }) => {
        // If setting is one of the Software Update settings, handle them differently
        let setting;
        switch (settingName) {
          case AUTO_UPDATE_LATEST:
          case UPDATE_SOURCE:
          case UPDATE_PATH:
          case CUSTOM_UPDATE_PATH:
          case CUSTOM_SUPPORT_UPDATE_PATH:
            // if any of these is changed/reverted, entire SW Update policy should be handled accordingly
            updatePolicyChange = updatePolicyChange ?? change;
            break;
          case POLLING_INTERVAL:
            setting = {
              name: getDeviceSettingName(settingName),
              paramType: PARAM_TYPE.PARAM,
              type: DEVICE_PARAM_TYPE.UINT
            };
            break;
          case SCHEDULE_UPDATE_CHECKS:
            setting = {
              name: getDeviceSettingName(settingName),
              paramType: PARAM_TYPE.PARAM,
              type: DEVICE_PARAM_TYPE.XBOOL
            };
            break;
          case UPDATE_START_TIME:
          case UPDATE_STOP_TIME:
            setting = {
              name: getDeviceSettingName(settingName),
              paramType: PARAM_TYPE.PARAM,
              type: DEVICE_PARAM_TYPE.STRING
            };
            break;
          default:
            setting = flatSettings[settingName];
            break;
        }

        if (setting) {
          const { name, paramType, type, index1, index2 } = setting;
          if (change === "reverted") {
            // if setting is reverted remove it from the policy
            configurationAttributes = configurationAttributes.filter(
              attr => attr.name !== name
            );
          } else {
            settingValue = convertValueToString(settingValue, type!);

            // update setting if it already exists in the policy, otherwise add it
            let configurationAttr = configurationAttributes.find(
              attr => attr.name === name
            );
            if (configurationAttr) {
              updateConfigurationAttribute(
                configurationAttr,
                settingValue,
                paramType!,
                index1,
                index2
              );
            } else {
              configurationAttr = createConfigurationAttribute(
                name!,
                settingValue,
                paramType!,
                index1,
                index2
              );

              if (configurationAttr) {
                configurationAttributes.push(configurationAttr);
              }
            }
          }
        }
      }
    );

    // Do the mutations
    const swMutation = swUpdatePolicy?.policyId ? updatePolicy : createPolicy;
    const settingsMutation = settingsPolicy?.policyId
      ? updatePolicy
      : createPolicy;

    let settingsError: CombinedError | undefined;
    if (configurationAttributes.length > 0) {
      // update/create policy
      const { __typename, ...settingsPolicyWithoutTypename } = settingsPolicy;
      let policyInput: PolicyInput = {
        ...settingsPolicyWithoutTypename,
        configurationAttributes,
        isExecuteImmediately: true,
        modifiedBy: user?.email
      };
      ({ error: settingsError } = await settingsMutation({
        policy: policyInput,
        tenantId: tenantId
      }));
    } else if (settingsPolicy?.policyId) {
      // all settings were reverted, delete policy
      ({ error: settingsError } = await deletePolicy({
        policyId: settingsPolicy.policyId
      }));
    }

    let updateError: CombinedError | undefined;

    if (updatePolicyChange === "reverted") {
      // delete policy (reverted)
      ({ error: updateError } = await deletePolicy({
        policyId: swUpdatePolicy?.policyId
      }));
    } else if (updatePolicyChange === "edited") {
      // create/update policy
      const swUpdateSource = values.autoUpdateLatest
        ? SwUpdateSource.StandardUrl
        : (values.swUpdateSource as SwUpdateSource);
      const updateFromPolyLens = swUpdateSource === SwUpdateSource.StandardUrl;
      const updateFromCustomServer =
        swUpdateSource === SwUpdateSource.CustomUrl;

      const { __typename, ...swUpdatePolicyWithoutTypename } = swUpdatePolicy;
      const updatePolicyInput: PolicyInput = {
        ...swUpdatePolicyWithoutTypename,
        swUpdatePath: values.autoUpdateLatest
          ? LATEST
          : updateFromPolyLens
          ? (values.swUpdatePath as string)
          : "",
        swSupportUpdatePath:
          !values.autoUpdateLatest && updateFromPolyLens
            ? getSupportUpdatePathInput(
                values.swSupportUpdatePath as SoftwareSupportUpdatePath[]
              )
            : null,
        customSwUpdatePath: updateFromCustomServer
          ? (values.customSwUpdatePath as string)
          : "",
        customSwSupportUpdatePath: updateFromCustomServer
          ? getSupportUpdatePathInput(
              values.customSwSupportUpdatePath as SoftwareSupportUpdatePath[]
            )
          : null,
        swUpdateSource,
        isExecuteImmediately: true,
        modifiedBy: user?.email,
        configurationAttributes: []
      };

      ({ error: updateError } = await swMutation(
        { policy: updatePolicyInput, tenantId: tenantId },
        { i18nKey: "ERROR_SAVING_DEVICE_POLICY" }
      ));
    }

    if (!settingsError && !updateError) {
      addNotification(
        getSuccessNotification(
          settingsPolicy?.policyDeploymentScope ??
            PolicyDeploymentScope.DeviceModel,
          emptyPolicy
        )
      );

      // in case of device level policy for PolyOS devices,
      // we want to call requestProvisioningSync to trigger an immediate application of the settings changes
      if (
        settingsPolicy?.policyDeploymentScope ===
          PolicyDeploymentScope.Device &&
        device
      ) {
        provisioningSync({ deviceId: device.id });
      }

      dispatch(resetChanges());
      dispatch(resetProvisionedDeviceSettingInheritance());
    }
  };

  const { Content } = Layout;

  const entityName = siteName || groupName || device?.name || undefined;
  const policyTitle = getPolicyTitle(
    settingsPolicy?.policyDeploymentScope ?? PolicyDeploymentScope.DeviceModel,
    entityName
  );
  const pageTitle = isPolicyView ? (
    <>
      {settingsPolicy?.deviceModel}{" "}
      <p className="inline-block device-header-name">{` | ${policyTitle}`}</p>
    </>
  ) : (
    <>
      {device?.hardwareModel}{" "}
      <p className="inline-block device-header-name">{` | ${device?.name ||
        device?.room?.name ||
        device?.macAddress}`}</p>
    </>
  );

  const unsavedChanges = getUnsavedChangesCount(unsavedSettings);

  const emptyPolicy =
    isPolicyView && !swUpdatePolicy?.policyId && !settingsPolicy?.policyId;

  return (
    <UnsafeFormik
      name="DeviceSettings"
      initialValues={values}
      customDimensions={{
        model: device?.hardwareModel,
        software_version: device?.softwareVersion
      }}
      enableReinitialize
      validate={validate}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={activeCallInterrupt(submit)}
    >
      {({ values, dirty, submitForm }) => {
        return (
          <ValidationController>
            <Form className="form pb-2 provisioned-settings">
              <ActiveCallConfirmModal {...rest} />
              <DeviceHeader
                device={device}
                pageTitle={pageTitle}
                hasActionButtons
                showConfirm={
                  settingsPolicy?.policyDeploymentScope ===
                    PolicyDeploymentScope.Device &&
                  swUpdatePolicy?.policyDeploymentScope ===
                    PolicyDeploymentScope.Device
                    ? isDeviceSwVersionChanged(unsavedSettings, values)
                    : false
                }
                unsavedChanges={unsavedChanges}
                onDiscard={() => dispatch(resetChanges())}
              >
                <div
                  className={`d-flex ${emptyPolicy ? "space-between" : "end"}`}
                >
                  {emptyPolicy && (
                    <div className="unchanged-policy-note">
                      {t("CHANGE_POLICY_TO_SAVE_NOTE")}
                    </div>
                  )}
                  <ErrorDisplay />
                </div>
              </DeviceHeader>
              <Route nest path="*/confirmApply">
                <ModalConfirm
                  title={t("CONFIRM_APPLY")}
                  onConfirm={async () => {
                    closeModal();
                    await submitForm();
                  }}
                  onCancel={closeModal}
                  open={true}
                  message={t("CONFIRM_DEVICE_LEVEL_POLICY_CHANGE")}
                  confirmLabel={t("OK")}
                />
              </Route>
              <Switch>
                <Route nest path="my-settings">
                  <MySettings
                    deviceSettings={settings}
                    updateSettings={swUpdateSettings}
                    device={device}
                    deviceModel={settingsPolicy.deviceModel ?? ""}
                    site={siteName}
                    group={groupName}
                    dirty={dirty || unsavedChanges > 0}
                  />
                </Route>
                <Route nest path="update">
                  <ProvisionedUpdateSoftware
                    id={device?.id}
                    model={swUpdatePolicy.deviceModel ?? ""}
                    settings={swUpdateSettings}
                    version={device?.softwareVersion ?? ""}
                    dirty={dirty || unsavedChanges > 0}
                  />
                </Route>
                <Route nest path=":category/:subcategory/:collection">
                  {({ match }) => {
                    const { category, subcategory, collection } = match?.params;
                    const settingsToDisplay =
                      settings[category]?.[subcategory]?.[collection];
                    if (!settingsToDisplay) {
                      return null;
                    }

                    return (
                      <Content.ScrollingList data-testId="settings">
                        <SettingsCategory
                          title={collection !== MAIN ? collection : subcategory}
                        >
                          {showLineWarning && (
                            <span className="text-md pl-75">
                              {t("LINE_WARNING")}
                              <NavLink
                                to={`/manage/inventory/details/${device?.id}/settings/${CALL_CONFIGURATION}/${GLOBAL_SETTINGS}`}
                              >
                                {t("GLOBAL_SETTINGS")}
                              </NavLink>
                              .
                            </span>
                          )}
                          <div className="details grid form mb-3">
                            <Settings
                              collection={collection}
                              settings={settingsToDisplay}
                              settingFilter={settingFilter ?? ""}
                              values={values}
                              defaultValues={defaultValues}
                            />
                          </div>
                        </SettingsCategory>
                      </Content.ScrollingList>
                    );
                  }}
                </Route>
                <Route nest path=":category?/:subcategory?">
                  {({ match }) => {
                    // this is used to match routes like "/", "/category" and "/category/subcategory",
                    // and redirect to the full path "/category/subcategory/collection"
                    let { category, subcategory } = match?.params;
                    let path = "";
                    if (!category) {
                      category = Object.keys(settings)[0];
                      path += "/:category";
                    }
                    if (!subcategory && settings[category]) {
                      subcategory = Object.keys(settings[category])[0];
                      path += "/:subcategory";
                    }
                    let collection = "";
                    if (settings[category]?.[subcategory]) {
                      collection = Object.keys(
                        settings[category]?.[subcategory]
                      )[0];
                      path += "/:collection";
                    }

                    if (!(category && subcategory && collection)) {
                      return;
                    }

                    return (
                      <Redirect
                        nest
                        persist={[
                          "PolicyFilterState",
                          "deviceModel",
                          "site",
                          "DeviceFilterState",
                          "InsightsFilterState",
                          "group",
                          "previous"
                        ]}
                        to={generatePath(path, {
                          category,
                          subcategory,
                          collection
                        })}
                      />
                    );
                  }}
                </Route>
              </Switch>
            </Form>
          </ValidationController>
        );
      }}
    </UnsafeFormik>
  );
};

const ValidationController: React.FC = ({ children }) => {
  const unsavedSettings = useSelector((s: State) => s.settings);
  const { values, validateForm } = useFormikContext();

  useEffect(() => {
    // perform validations when unsavedSettings are changed, in addition to changes to values
    validateForm(values);
  }, [unsavedSettings, validateForm, values]);

  return <>{children}</>;
};

export type SectionSetting = Setting & {
  show: boolean;
  collection: string;
};
type SectionSettings = {
  [subgrouping: string]: SectionSetting[];
};
type Section = {
  title: string;
  settings: SectionSettings;
};
type SectionProps = Section & {
  first: boolean;
};

const Settings: React.FC<{
  collection: string;
  settings: {
    [grouping: string]:
      | {
          [subGrouping: string]: Setting[];
        }
      | Setting[];
  };
  settingFilter: string;
  values: SettingsValues;
  defaultValues: SettingsValues;
}> = ({ collection, settings, settingFilter, values, defaultValues }) => {
  const { t } = useTranslation();

  const getSectionSettings = (settings: Setting[], filter: string) => {
    return settings
      .filter(setting => !filter || filter === setting.settingName)
      .map(setting => {
        const { lensConditional, lensConditionalValue } = setting;
        let show = true;
        if (lensConditional) {
          // for lensConditional setting we don't know its type
          // which is an issue in case of bool type since we have 2 of them (Bool and xBool)
          // we will use lensConditionalValue(for bools either "0"/"1" or "True"/"False") to figure it out
          // for other types we don't care
          let type = "";
          if (
            lensConditionalValue === "True" ||
            lensConditionalValue === "False"
          ) {
            type = DEVICE_PARAM_TYPE.XBOOL;
          } else if (
            lensConditionalValue === "0" ||
            lensConditionalValue === "1"
          ) {
            type = DEVICE_PARAM_TYPE.BOOL;
          }

          const value = convertValueToString(
            values[lensConditional.toLowerCase()],
            type
          );

          show = value === lensConditionalValue;
        }

        const defaultValue = convertValueToString(
          defaultValues[setting.settingName],
          setting.type!
        );

        return {
          ...setting,
          defaultValue,
          collection,
          show
        };
      });
  };

  const sections: Section[] = [];
  Object.keys(settings).forEach(grouping => {
    let sectionSettings: SectionSettings = {};
    const groupSettings = settings[grouping];
    if (Array.isArray(groupSettings)) {
      sectionSettings[DEFAULT_SUBGROUPING] = getSectionSettings(
        groupSettings,
        settingFilter
      );
    } else {
      Object.entries(groupSettings).forEach(
        ([subgrouping, subgroupSettings]) => {
          sectionSettings[subgrouping] = getSectionSettings(
            subgroupSettings,
            settingFilter
          );
        }
      );
    }

    if (
      Object.values(sectionSettings)
        .flat()
        .filter(s => s.show).length !== 0
    ) {
      sections.push({
        title: grouping,
        settings: sectionSettings
      });
    }
  });

  if (sections.length === 0) {
    return <NoResultsIndicator title={t("NO_SETTINGS")} />;
  }

  return (
    <>
      {sections.map((section, index) => {
        const { title } = section;
        return (
          <SettingsSection
            key={title + "-key"}
            {...section}
            first={index === 0}
          />
        );
      })}
    </>
  );
};

const SettingsSection: React.FC<SectionProps> = ({
  title,
  first,
  settings
}) => {
  const { t } = useTranslation();
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();
  const showInheritance = policyScope === PolicyDeploymentScope.Device;
  const showTitle = title && title !== NO_GROUPING;
  return (
    <>
      {!first && <hr />}
      <div className="legend grid">
        {showTitle && <div className="col-7">{title}</div>}
        {first && showInheritance && (
          <div className={`${!showTitle ? "offset-7" : ""} col-5`}>
            {t("POLICY_INHERITANCE")}
          </div>
        )}
      </div>
      {Object.entries(settings).map(([subgrouping, subgroupSettings]) => {
        return (
          <div key={`${title}-subgroup-${subgrouping}`}>
            {subgrouping !== DEFAULT_SUBGROUPING && (
              <div className="legend text-md pl-3 pt-1">{subgrouping}</div>
            )}
            {subgroupSettings.map(setting => {
              const { settingName, show } = setting;
              if (!settingName) return null;

              return (
                <div
                  key={settingName + "-setting"}
                  className={`show-hide ${show ? "show" : "hide"}`}
                  data-testid="provisioned-setting"
                >
                  <ProvisionedSetting setting={setting} />
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
};

export interface RevertProps {
  action?: () => void;
  icon?: string;
  iconClassName?: string;
}
interface CommonSettingProps {
  setting: SectionSetting;
  hasInput?: boolean;
  simpleSetting?: boolean;
  revert?: RevertProps;
  disabled?: boolean;
}

interface FormSettingProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    CommonSettingProps {
  name: string;
}

interface LineSettingProps extends CommonSettingProps {
  name: string;
}

export const ProvisionedSetting: React.FC<CommonSettingProps> = ({
  setting,
  revert
}) => {
  const { settingName } = setting;
  const inputName = `['${settingName}']`;
  return settingName === LINE_LIMIT_PARAM ? (
    <LineLimitSetting name={inputName} setting={setting} revert={revert} />
  ) : (
    <FormSetting name={inputName} setting={setting} revert={revert} />
  );
};

export const FormSetting: React.FC<FormSettingProps> = ({
  name,
  setting,
  revert,
  disabled,
  hasInput = true,
  simpleSetting = false,
  ...rest
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const firstUpdate = useRef(true);
  let userCanEdit = useGuard(needs("update:device"));
  // add in user roles debug check to render tooltip for provisioned devices
  const debugUserRoles = useSelector((state: State) => {
    return state.env.debugUserRoles;
  });
  const [
    {
      externalPolicyLastSuccessfullyUpdated,
      externalPolicyLastSuccessfullyUpdatedTime
    }
  ] = useProvisionedSettingsContext();

  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();
  const inheritance = useSelector(
    (state: State) =>
      state.provisionedDeviceSettingInheritance[
        setting?.name?.toLocaleLowerCase() ?? ""
      ]
  );
  const [, { initialValue, value }] = useField(name);
  const {
    category,
    subcategory,
    collection,
    settingName,
    friendlyName,
    help,
    min,
    max,
    defaultValue,
    featureCategory
  } = setting;

  const isExternalPolicySetting =
    setting.policyDeploymentScope === PolicyDeploymentScope.ExternalDevice;

  const changed = useSelector(
    (s: State) =>
      s.settings[category]?.[subcategory]?.[collection]?.[settingName]
  );

  const modelName = useSelector(
    (s: State) => s.provisionedDeviceSettingInheritance["hardwareModel"]
  )?.value;

  const handleDispatchChangeDebounce = useRef(
    debounce((change: Change) => {
      // dispatch setting change action so we can show indicator in the settings sidebar nav
      dispatch(
        settingChanged({
          category,
          subcategory,
          collection,
          settingName,
          change
        })
      );
    }, 300)
  ).current;

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    handleDispatchChangeDebounce(value !== initialValue ? "edited" : "");
  }, [value, initialValue, handleDispatchChangeDebounce]);

  const badges: Record<string, ReactNode> = {
    [FeatureCategory.Beta]: <BetaBadge />,
    [FeatureCategory.Preview]: <PreviewBadge />
  };

  const getBadge = (featureCategory: FeatureCategory, className: string) => {
    const badge = badges[featureCategory];

    return React.isValidElement(badge)
      ? React.cloneElement(badge, { className })
      : null;
  };

  const getSettingInput = (placeholder: string) => {
    const { type, enumVal } = setting;
    // settings coming from external device policies (i.e. Microsoft Teams) should be marked as locked (not changeable on Lens)
    const isDisabled = !userCanEdit || disabled || isExternalPolicySetting;

    switch (type) {
      case DEVICE_PARAM_TYPE.ENUM:
        let enumOptions: Array<OptionType> = [];
        let userFriendlyPlaceholder = placeholder;

        enumVal
          ?.filter((opt): opt is EnumVal => opt !== null && opt.value !== null)
          .forEach(opt => {
            enumOptions.push({
              value: opt.value!,
              text: opt.friendlyValue || opt.value!,
              icon: getBadge(
                opt.featureCategory ?? FeatureCategory.Released,
                "ml-5"
              ),
              iconPosition: "after"
            });
            // placeholder holds the value, but in most cases, different (user friendly) value is displayed in the dropdown
            // therefore, placeholder value will be matched to friendlyValue if possible, and that friendlyValue will be used
            if (opt.value === placeholder && opt.friendlyValue) {
              userFriendlyPlaceholder = opt.friendlyValue;
            }
          });

        const selectedOption = enumOptions.find(opt => opt.value === value);
        const inputReplacement = selectedOption?.icon ? (
          <span className="d-flex">
            <span>{selectedOption.text}</span>
            {selectedOption.icon}
          </span>
        ) : null;

        return (
          <>
            <Select
              placeholder={userFriendlyPlaceholder}
              name={name}
              options={enumOptions}
              disabled={isDisabled}
              className="provisioned-setting"
              inputReplacement={inputReplacement}
            />
            {debugUserRoles && (
              <InactiveInputToolTip permission="update:device" />
            )}
          </>
        );
      case DEVICE_PARAM_TYPE.BOOL:
      case DEVICE_PARAM_TYPE.XBOOL:
        return (
          <>
            <ToggleSwitch name={name} disabled={isDisabled} />
            {debugUserRoles && (
              <InactiveInputToolTip permission="update:device" />
            )}
          </>
        );
      case DEVICE_PARAM_TYPE.UINT:
      case DEVICE_PARAM_TYPE.UINT_NULL:
      case DEVICE_PARAM_TYPE.SINT:
      case DEVICE_PARAM_TYPE.SINT_NULL:
        return (
          <>
            <Input
              type="number"
              min={min ?? ""}
              max={max ?? ""}
              name={name}
              disabled={isDisabled}
              placeholder={placeholder}
              {...rest}
            />
            {debugUserRoles && (
              <InactiveInputToolTip permission="update:device" />
            )}
          </>
        );
      case DEVICE_PARAM_TYPE.STRING:
        return (
          <>
            <Input
              type="text"
              name={name}
              disabled={isDisabled}
              placeholder={placeholder}
              resourceSelector
              {...rest}
            />
            {debugUserRoles && (
              <InactiveInputToolTip permission="update:device" />
            )}
          </>
        );
      case DEVICE_PARAM_TYPE.STRING_PASSWORD:
        return (
          <>
            <Input
              type="password"
              name={name}
              disabled={isDisabled}
              placeholder={placeholder}
              {...rest}
            />
            {debugUserRoles && (
              <InactiveInputToolTip permission="update:device" />
            )}
          </>
        );
      default:
        return (
          <>
            <Input
              type="text"
              name={name}
              disabled={isDisabled}
              placeholder={placeholder}
              {...rest}
            />
            {debugUserRoles && (
              <InactiveInputToolTip permission="update:device" />
            )}
          </>
        );
    }
  };
  const showInheritance = policyScope === PolicyDeploymentScope.Device;
  const inheritanceLabel = useInheritanceLabel(
    changed,
    inheritance,
    policyScope,
    modelName
  );
  const reverted = changed === "reverted";
  const label = friendlyName || settingName;
  return (
    <div className="form-group" data-testid="form-setting">
      <label className="col-4 text-right">
        {!simpleSetting && changed && <SettingChangeIndicator />}
        {help && (
          <ToolTip
            size="lg"
            position="bottom"
            icon="help_circle"
            iconTitle="help"
            iconPosition="left"
            className="text-left settings-help"
          >
            <p>{help}</p>
          </ToolTip>
        )}
        {label}:
      </label>
      <div className="col-3" data-testid={settingName + "-setting"}>
        <div className="d-flex">
          {hasInput && getSettingInput(defaultValue ?? "")}
          {!simpleSetting &&
            getBadge(featureCategory ?? FeatureCategory.Released, "ml-5")}
        </div>
      </div>
      {(showInheritance || reverted) && (
        <div className="col-3 text-md">
          {!simpleSetting && (
            <div data-testid="inheritance">
              {isExternalPolicySetting &&
                externalPolicyLastSuccessfullyUpdated === false && (
                  <ToolTip
                    icon="warning_triangle"
                    iconTitle=""
                    position="right"
                    size="md"
                    className="warning ml-5"
                  >
                    <span>
                      {t("EXTERNAL_DEVICE_POLICY_STALE")}
                      {externalPolicyLastSuccessfullyUpdatedTime
                        ? ` ${t(
                            "EXTERNAL_DEVICE_POLICY_LAST_SUCCESSFUL_UPDATE",
                            {
                              time: format(
                                externalPolicyLastSuccessfullyUpdatedTime,
                                DATE_FORMAT.WITH_TIME_LONG
                              )
                            }
                          )}`
                        : ""}
                    </span>
                  </ToolTip>
                )}
              {inheritanceLabel}
            </div>
          )}
        </div>
      )}
      <div className="col-2">
        {!simpleSetting && (
          <SettingIcon
            settingName={setting.friendlyName ?? setting.name ?? ""}
            revert={revert}
            locked={isExternalPolicySetting}
          />
        )}
      </div>
    </div>
  );
};

const LineLimitSetting: React.FC<LineSettingProps> = ({
  name,
  setting,
  revert
}) => {
  const [{ maxConfiguredLine }] = useProvisionedSettingsContext();
  const [settingValue, setSettingValue] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [{ onChange }, { initialValue }, { setValue }] = useField(name);

  const handleLineLimitChangeDebounce = useRef(
    debounce((val: string, maxConfiguredLine: number) => {
      const value = parseInt(val);
      if (!isNaN(value)) {
        setSettingValue(value);
        if (value >= 1 && value < maxConfiguredLine) {
          setShowWarning(true);
        }
      }
    }, 500)
  ).current;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    handleLineLimitChangeDebounce(e.target.value, maxConfiguredLine);
  };

  return (
    <>
      <FormSetting
        name={name}
        setting={setting}
        onChange={handleChange}
        revert={revert}
      />
      <ChangeLineLimitWarningModal
        open={showWarning}
        maxConfiguredLine={maxConfiguredLine}
        value={settingValue}
        onConfirm={() => {
          setShowWarning(false);
        }}
        onCancel={() => {
          setValue(initialValue);
          setShowWarning(false);
        }}
      />
    </>
  );
};

const ChangeLineLimitWarningModal: React.FC<{
  open: boolean;
  value: number;
  maxConfiguredLine: number;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, value, maxConfiguredLine, onConfirm, onCancel }) => {
  const { t } = useTranslation();

  const lineStart = value + 1;
  const lineEnd = maxConfiguredLine;

  const linesText =
    lineEnd === lineStart
      ? t("MAXIMUM_LINE_REGISTRATION_NOTE_SINGLE_LINE", { line: lineStart })
      : t("MAXIMUM_LINE_REGISTRATION_NOTE_MULTI_LINE", { lineStart, lineEnd });

  return (
    <ModalConfirm
      title={t("MAXIMUM_LINE_REGISTRATION")}
      message={t("MAXIMUM_LINE_REGISTRATION_NOTE", {
        linesConfigured: maxConfiguredLine,
        value,
        lines: linesText
      })}
      onConfirm={onConfirm}
      onCancel={onCancel}
      open={open}
      confirmLabel={t("OK")}
    />
  );
};

export const SettingIcon: React.FC<{
  settingName: string;
  locked?: boolean;
  revert?: RevertProps;
}> = ({ settingName, locked = false, revert }) => (
  <div className="setting-icon">
    {locked ? (
      <PolyIcon
        icon="locked"
        className="white"
        data-testId={`lock-${settingName}`}
      />
    ) : (
      revert?.action && (
        <PolyIconButton
          icon={revert.icon ?? "trash"}
          iconClassName={revert?.iconClassName ?? ""}
          onClick={revert?.action}
          type="button"
          data-testid={`trash-${settingName}`}
        />
      )
    )}
  </div>
);

export default DeviceSettingsLoader;
